import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppState } from '../../App';
import { useChat } from '../../libs/hooks';
import useScreenSize from '../../libs/hooks/useScreenSize';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { setNotifications } from '../../redux/features/app/appSlice';
import {
    setInitialChatsLoaded,
    setIsLoadingChats,
} from '../../redux/features/conversations/conversationsSlice';
import { useTheme } from '../../theme/ThemeContext';
import { AppBar } from './AppBar/AppBar';
import { ChatView } from './ChatView/ChatView';
import { NOTIFICATION_TEMPLATES } from './ChatView/Notifications/NotificationConstants';

interface IAppContainer {
    setAppState: (state: AppState) => void;
    isChatListHidden: boolean;
}

export const AppContainer: React.FC<IAppContainer> = ({ setAppState, isChatListHidden }) => {
    const dispatch = useDispatch();
    const chat = useChat();
    const screenSize = useScreenSize();
    const [chatMode, setChatMode] = useState('Bubbles');
    const [isChatListOpen, setIsChatListOpen] = useState(!isChatListHidden && screenSize.width > 1024 ? true : false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const { conversations, initialChatsLoaded } = useAppSelector((state: RootState) => state.conversations);

    const changeChatMode = (mode: string) => {
        setChatMode(mode);
        dispatch(
            setNotifications([
                {
                    ...NOTIFICATION_TEMPLATES.CHAT_MODE_CHANGE,
                    description: NOTIFICATION_TEMPLATES.CHAT_MODE_CHANGE.description.replace('[[MODE]]', mode),
                },
            ]),
        );
    };

    const toggleChatList = () => {
        if (screenSize.width > 639 && screenSize.width < 768 && isSettingsOpen) {
            setIsSettingsOpen(false);
        }
        setIsChatListOpen(!isChatListOpen);
    };

    const toggleSettings = () => {
        if (screenSize.width > 639 && screenSize.width < 768 && isChatListOpen) {
            setIsChatListOpen(false);
        }
        setIsSettingsOpen(!isSettingsOpen);
    };

    const { theme, toggleTheme } = useTheme();

    const toggleDarkMode = () => {
        toggleTheme();
    };

    if (theme === 'dark') {
        document.body.classList.add('dark');
    } else {
        document.body.classList.remove('dark');
    }

    useEffect(() => {
        localStorage.removeItem('isLoggingIn');

        if (!isChatListHidden && !initialChatsLoaded) {
            dispatch(setIsLoadingChats(true));
            void chat.loadChats(conversations).then(() => {
                dispatch(setIsLoadingChats(false));
                dispatch(setInitialChatsLoaded(true));
            });
        }
    }, []);

    return (
        <div className={`h-dvh w-dvh flex flex-col-reverse sm:flex-row`}>
            <AppBar
                isDarkMode={theme === 'dark'}
                isChatListOpen={isChatListOpen}
                isSettingsOpen={isSettingsOpen}
                toggleDarkMode={toggleDarkMode}
                toggleChatList={toggleChatList}
                toggleSettings={toggleSettings}
                setLoadingState={() => {
                    setAppState(AppState.SigningOut);
                }}
            />
            {/* <MobileAppBar
                isDarkMode={theme === 'dark'}
                isChatListOpen={isChatListOpen}
                isSettingsOpen={isSettingsOpen}
                toggleDarkMode={toggleDarkMode}
                toggleChatList={toggleChatList}
                toggleSettings={toggleSettings}
                setLoadingState={() => {
                    setAppState(AppState.SigningOut);
                }}
            /> */}
            <ChatView
                isChatListOpen={isChatListOpen}
                isSettingsOpen={isSettingsOpen}
                isDarkMode={theme === 'dark'}
                chatMode={chatMode}
                toggleDarkMode={toggleDarkMode}
                changeChatMode={changeChatMode}
                toggleSettings={toggleSettings}
                toggleChatList={toggleChatList}
            />
        </div>
    );
};
